/**
 * staff-ui.tsx — Mảnh giao diện chung của màn nhân viên AI: khung một mục (tiêu đề + nhãn "Cần phát hành" /
 * "Hiệu lực ngay" + nút), nhãn phạm vi, khối con có tiêu đề.
 */
import type { ReactNode } from 'react'
import { Rocket, Zap } from 'lucide-react'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/misc'
import { cn } from '@/lib/utils'
import { SCOPE_HINT, SCOPE_LABEL, type SectionScope } from './staff-lib'

export function ScopeBadge({ scope, className }: { scope: SectionScope; className?: string }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className={cn(
            'inline-flex shrink-0 cursor-help items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium',
            scope === 'live' ? 'bg-success/10 text-success' : 'bg-warning/10 text-warning',
            className,
          )}
        >
          {scope === 'live' ? <Zap className="h-3 w-3" /> : <Rocket className="h-3 w-3" />}
          {SCOPE_LABEL[scope]}
        </span>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-xs text-xs">{SCOPE_HINT[scope]}</TooltipContent>
    </Tooltip>
  )
}

/** Khung một mục của màn nhân viên AI. */
export function StaffPanel({ id, title, description, icon, scope, actions, children }: {
  id?: string
  title: ReactNode
  description?: ReactNode
  icon?: ReactNode
  scope?: SectionScope
  actions?: ReactNode
  children: ReactNode
}) {
  return (
    <section id={id} className="scroll-mt-4 rounded-xl border bg-background">
      <div className="flex flex-wrap items-start gap-2 border-b px-4 py-3">
        {icon && <span className="mt-0.5 shrink-0 text-primary">{icon}</span>}
        <div className="min-w-[200px] flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-sm font-semibold">{title}</h2>
            {scope && <ScopeBadge scope={scope} />}
          </div>
          {description && <p className="mt-0.5 text-xs text-muted-foreground">{description}</p>}
        </div>
        {actions && <div className="flex shrink-0 flex-wrap items-center gap-1">{actions}</div>}
      </div>
      <div className="space-y-4 px-4 py-4">{children}</div>
    </section>
  )
}

/** Khối con trong một mục (tiêu đề nhỏ + mô tả + nội dung). */
export function SubBlock({ id, title, description, scope, actions, children, className }: {
  id?: string
  title: ReactNode
  description?: ReactNode
  scope?: SectionScope
  actions?: ReactNode
  children: ReactNode
  className?: string
}) {
  return (
    <div id={id} className={cn('scroll-mt-4 space-y-2', className)}>
      <div className="flex flex-wrap items-start gap-2">
        <div className="min-w-[180px] flex-1">
          <p className="flex flex-wrap items-center gap-2 text-sm font-medium">{title}{scope && <ScopeBadge scope={scope} />}</p>
          {description && <p className="text-xs text-muted-foreground">{description}</p>}
        </div>
        {actions}
      </div>
      {children}
    </div>
  )
}
